import Bugsnag from '@bugsnag/js';
import { isOfflineModeEnabled } from '../composables/useOfflineDetection';

/**
 * Service worker lifecycle for the offline platform layer (TRIP-2666).
 *
 * Booted from app.js on `window` load (the `app` entry runs on legacy Twig
 * pages too, so registration heals from anywhere), and re-synced from App.vue
 * whenever app_init refreshes the `offline_mode` flag:
 *   - flag ON  → register /service-worker.js (same-origin, scope '/')
 *   - flag OFF → message the worker to purge its caches, unregister, and
 *     belt-and-suspenders delete the tw-* caches from the page. This is the
 *     NO-DEPLOY KILL SWITCH: turning the beta flag off server-side unwinds
 *     every client on its next app_init.
 *
 * Never registered under local HMR (`NODE_ENV === 'development'`) by default —
 * the worker is normally pure noise locally. Escape hatch for exercising the
 * offline layer in dev: localStorage.sw_dev_force = '1', then reload. With the
 * hatch on, the asset routes DO cache the :8080 dev bundles (the `webpack-build`
 * rule in assets.js, hot-update chunks excluded) so offline genuinely works —
 * at the cost of HMR freshness, an acceptable trade while debugging offline.
 *
 * Update cadence: browsers check on navigation and ~daily, but Manifest tabs
 * run all day on kiosk devices — we also call registration.update() when the
 * tab becomes visible and on a 60-minute timer. No update prompt and no
 * controllerchange reload: there is no precache manifest, HTML is
 * NetworkFirst and bundles are content-hashed, so a new worker activating
 * mid-session cannot skew versions (see assets/sw/service-worker.js header).
 */

const SW_URL = '/service-worker.js';
const UPDATE_INTERVAL_MS = 60 * 60 * 1000;
const PURGE_ACK_TIMEOUT_MS = 3000;
const CACHE_PREFIX = 'tw-';

let updateChecksBound = false;

function isSupported() {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    window.isSecureContext
  );
}

function isEnvironmentAllowed() {
  if (process.env.NODE_ENV !== 'development') return true;
  try {
    return localStorage.getItem('sw_dev_force') === '1';
  } catch (_e) {
    return false;
  }
}

function bindUpdateChecks(registration) {
  if (updateChecksBound) return;
  updateChecksBound = true;

  const safeUpdate = () => registration.update().catch(() => {});
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && isOfflineModeEnabled()) safeUpdate();
  });
  setInterval(() => {
    if (isOfflineModeEnabled()) safeUpdate();
  }, UPDATE_INTERVAL_MS);
}

function isManifestPage() {
  try {
    return window.location.pathname === '/manifest' || window.location.pathname.startsWith('/manifest/');
  } catch (_e) {
    return false;
  }
}

function warmCurrentShell(registration) {
  if (!isOfflineModeEnabled() || !isManifestPage()) return;
  // controller is null on a first visit (the worker doesn't control the page
  // that registered it); registration.active works without control.
  const worker = navigator.serviceWorker.controller || (registration && registration.active);
  if (!worker) return;
  try {
    worker.postMessage({ type: 'TW_SW_WARM_SHELL', url: window.location.href });
  } catch (_e) {
    // Best-effort only; offline refresh still falls back to the worker's navigation path.
  }
}

// Lazy chunks behind offline-critical UI. Warmed (not SW-precached) so the SW
// stays manifest-free: we invoke the app's OWN dynamic import, which loads the
// exact content-hashed chunk via the same cors-mode request the modal would —
// the SW's runtime CacheFirst (assets.js) then stores a copy the later modal
// load can actually use. Best-effort; the inline degrade covers a miss.
function warmOfflineChunks() {
  // Only worth it once the SW controls the page (else the fetch isn't cached)
  // and only with the offline feature on.
  if (!isOfflineModeEnabled() || !navigator.serviceWorker.controller) return;
  const warm = () => {
    // Booking modal (Trip.vue) — the screen most likely opened offline from a
    // cached manifest. Same specifier App.vue uses → same webpack chunk.
    import('../components/trip/Trip').catch(() => {});
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(warm, { timeout: 5000 });
  } else {
    setTimeout(warm, 3000);
  }
}

// Remember the manifest URL the user is on so the offline fallback page
// (public/offline.html) can link straight back to a screen that actually has
// cached data. Bare /manifest resolves to today, which may have nothing cached.
function rememberManifestUrl() {
  if (!isOfflineModeEnabled() || !isManifestPage()) return;
  try {
    localStorage.setItem('tw_last_manifest_url', window.location.href);
  } catch (_e) {
    // Private mode / storage disabled — best effort.
  }
}

// Make a single online visit enough to render offline: hand the worker the
// content-hashed /build/ bundles this page loaded (entry + route + shared
// chunks) so it caches them. On the first visit the worker never controls the
// page, so the runtime cache never sees these requests — this closes that gap by
// fetching them in the worker directly. Sources are unioned: the Resource Timing
// buffer (every /build/ file the document actually fetched, even ones whose
// <script> webpack already removed) plus a MutationObserver + DOM sweep for tags
// added later. hot-update chunks are excluded so HMR is unaffected.
function precacheLoadedBundles(registration) {
  if (!isOfflineModeEnabled() || !isManifestPage()) return;
  // controller is null on a first visit (the worker doesn't control the page
  // that registered it); registration.active accepts messages without control.
  const worker = navigator.serviceWorker.controller || (registration && registration.active);
  if (!worker) return;

  // The manifest fires a lot of requests; grow the Resource Timing buffer so the
  // early /build/ chunk entries aren't evicted before we read them below.
  try {
    if (window.performance && performance.setResourceTimingBufferSize) {
      performance.setResourceTimingBufferSize(1000);
    }
  } catch (_e) {
    // Best-effort only.
  }

  const sent = new Set();
  const post = (candidates) => {
    const urls = candidates.filter((u) => u && u.includes('/build/') && !u.includes('hot-update') && !sent.has(u));
    if (urls.length === 0) return;
    urls.forEach((u) => sent.add(u));
    try {
      worker.postMessage({ type: 'TW_SW_PRECACHE_ASSETS', urls });
    } catch (_e) {
      // Best-effort priming only.
    }
  };
  // webpack removes a dynamically-imported chunk's <script> once it loads, so DOM
  // scraping alone misses lazy route/shared chunks (their persistent <link> CSS
  // is found, the <script> JS is not — the "blank manifest offline" gap). The
  // Resource Timing buffer records every /build/ file the document actually
  // fetched (and it survives a soft SPA nav, since that's the same document), so
  // it recovers the chunks DOM scraping can't see. (TRIP-2666)
  const fromResourceTiming = () => {
    try {
      if (!window.performance || !performance.getEntriesByType) return [];
      return performance.getEntriesByType('resource').map((entry) => entry.name);
    } catch (_e) {
      return [];
    }
  };
  const sweep = () => {
    const nodes = document.querySelectorAll('script[src*="/build/"], link[rel="stylesheet"][href*="/build/"]');
    const domUrls = Array.from(nodes).map((el) => el.src || el.href);
    post([...domUrls, ...fromResourceTiming()]);
  };

  // Cache each chunk the INSTANT webpack injects its tag — a fixed-interval sweep
  // races the lazy route chunks, so a reload right after the page "looks loaded"
  // (a cold cache) misses the ones not yet swept. The observer keeps the cache in
  // lockstep with what the page has loaded.
  let observer = null;
  if (typeof MutationObserver === 'function') {
    observer = new MutationObserver((mutations) => {
      const urls = [];
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && (node.tagName === 'SCRIPT' || node.tagName === 'LINK')) {
            urls.push(node.src || node.href);
          }
        }
      }
      post(urls);
    });
    try {
      observer.observe(document.documentElement, { childList: true, subtree: true });
    } catch (_e) {
      observer = null;
    }
  }

  sweep(); // entry + anything already in the DOM
  // Backstop sweeps for late tags and MutationObserver-less environments; the
  // observer covers the timing-critical window above.
  [3000, 10000].forEach((delay) => setTimeout(sweep, delay));
  setTimeout(() => {
    if (observer) observer.disconnect();
  }, 20000);
}

// Once-per-page-load guard for the heavy warm/precache work below.
let manifestPrimed = false;

/**
 * Prime the manifest offline layer: remember the current manifest URL, warm the
 * shared shell, and precache the bundles the page loaded.
 *
 * Called on a full page load (register, below) AND from router.afterEach
 * (app.js) so arriving at the manifest via a SOFT SPA navigation primes too.
 * That path fires no page load and no `navigate` request, so without this the
 * worker never caches the shell and the first offline refresh falls back to
 * offline.html with the Manifest link disabled (TRIP-2666).
 *
 * `ready` (not `controller`) is awaited on purpose: on a first visit the worker
 * does not control the page that registered it, so gating on control skipped
 * priming entirely; `ready` resolves once an active worker matches our scope,
 * controlled or not, and priming messages go to registration.active.
 *
 * rememberManifestUrl runs on EVERY manifest entry (keeps the offline fallback's
 * deep-link on the last date viewed); the heavier warm/precache runs once per
 * page-load lifetime so repeated afterEach navigations don't stack shell fetches
 * or MutationObservers.
 */
export function primeManifestOffline() {
  if (!isSupported() || !isEnvironmentAllowed()) return;
  if (!isOfflineModeEnabled() || !isManifestPage()) return;
  navigator.serviceWorker.ready
    .then((registration) => {
      // Re-check after the async gap: a fast nav away must not prime (or mark
      // primed) a screen we've already left.
      if (!isOfflineModeEnabled() || !isManifestPage()) return;
      rememberManifestUrl();
      if (manifestPrimed) return;
      manifestPrimed = true;
      warmCurrentShell(registration);
      warmOfflineChunks();
      precacheLoadedBundles(registration);
    })
    .catch(() => {});
}

async function register() {
  const registration = await navigator.serviceWorker.register(SW_URL);
  bindUpdateChecks(registration);
  primeManifestOffline();
}

/** Ask the active worker to purge (it also clears the query_cache IDB). */
function requestPurge(registration) {
  return new Promise((resolve) => {
    const worker = registration.active;
    if (!worker) {
      resolve();
      return;
    }
    const channel = new MessageChannel();
    const timer = setTimeout(resolve, PURGE_ACK_TIMEOUT_MS);
    channel.port1.onmessage = () => {
      clearTimeout(timer);
      resolve();
    };
    try {
      worker.postMessage({ type: 'TW_SW_PURGE' }, [channel.port2]);
    } catch (_e) {
      clearTimeout(timer);
      resolve();
    }
  });
}

/** Page-side fallback in case the worker never answered the purge message. */
export async function deleteTwCaches() {
  if (typeof caches === 'undefined') return;
  const names = await caches.keys();
  await Promise.all(names.filter((name) => name.startsWith(CACHE_PREFIX)).map((name) => caches.delete(name)));
}

async function unregisterAndPurge() {
  const registration = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!registration) return;
  await requestPurge(registration);
  await registration.unregister();
  await deleteTwCaches();
}

export async function syncServiceWorkerRegistration() {
  if (!isSupported() || !isEnvironmentAllowed()) return;
  try {
    if (isOfflineModeEnabled()) {
      await register();
    } else {
      await unregisterAndPurge();
    }
  } catch (err) {
    // Never break app boot, but make the failure observable — a silently
    // failing registration/purge would take the offline layer (and the
    // flag-off PII cleanup) down without anyone knowing.
    if (typeof Bugsnag !== 'undefined' && Bugsnag.notify) {
      Bugsnag.notify(err, (event) => event.addMetadata('offline', { stage: 'syncServiceWorkerRegistration' }));
    }
  }
}
